import {
    AuthStrategyType,
    BlockKey,
    IndicatorFormat,
    IndicatorKey,
    IndicatorStyle,
    InspectionTab,
    ItemExitMode,
    LogoSource,
    PolygonEditingMode,
    PolygonTool,
    RouteKey,
    TenantConfig,
    TenantId
} from '../types';
import { createTasacionesApi } from './api/TasacionesApi';

/**
 * Perfil del tenant `tasaciones`: login OAuth2 (lista y detalle de siniestros) y vista de suscripción por token en
 * la URL (`/item/:id/:token`, sin login, etapa E4). El header toma logo y colores de la aseguradora (`customer` de
 * `/me/`, etapa E5).
 */
export const tasacionesConfig: TenantConfig = {
    id: TenantId.Tasaciones,
    session: {
        type: AuthStrategyType.Login,
        loginRoute: RouteKey.Login,
        landingRoute: RouteKey.ClaimsList
    },
    branding: {
        title: 'Sistema Geográfico de Siniestros',
        titleBold: true,
        documentTitle: 'Gestión de Polígonos',
        logo: { source: LogoSource.FromMe },
        colors: {
            primary: '#3C4A66',
            info: '#003781',
            brandBackground: '#003781'
        },
        indicatorStyle: IndicatorStyle.Compact,
        floatingComboboxLabels: true
    },
    api: createTasacionesApi,
    // `Item` es la vista de suscripción por token (`RouteAccess.UrlToken`); lista y detalle son de sesión.
    routes: [RouteKey.Login, RouteKey.DeleteAccount, RouteKey.Item, RouteKey.ClaimsList, RouteKey.ClaimDetail],
    features: {
        editPolygonInClaim: true,
        splitPolygon: true,
        samplingReport: false,
        eventDateLine: true,
        satelliteMapByDefault: false,
        noteAuthor: true
    },
    map: {
        polygonEditing: PolygonEditingMode.Selection,
        fitBoundsPadding: true
    },
    analysis: {
        zonificationScales: [2, 3, 4, 5, 6, 7, 8, 9, 10],
        ndviDiffScales: [3, 5, 7, 9, 11]
    },
    pages: {
        // Vista de suscripción por token: se comporta igual que la de Zurich (coordenadas, sumar/sobrescribir el mapa
        // general, centroide de la `redirect_url`, volver a la `redirect_url` del PATCH al guardar o salir).
        item: {
            indicators: [
                { key: IndicatorKey.CropType, label: 'Tipo de cultivo' },
                { key: IndicatorKey.DeclaredArea, label: 'Área declarada', format: IndicatorFormat.Hectares, decimals: 1 },
                { key: IndicatorKey.Department, label: 'Departamento' },
                { key: IndicatorKey.Province, label: 'Provincia' },
                { key: IndicatorKey.Season, label: 'Campaña' }
            ],
            polygonTools: [
                PolygonTool.PreloadedItems,
                PolygonTool.PointSearch,
                PolygonTool.Draw,
                PolygonTool.Coordinates,
                PolygonTool.UploadFile
            ],
            mergeLoadedPolygons: true,
            centroidFromRedirectUrl: true,
            exitMode: ItemExitMode.RedirectFromSaveResponse,
            blocks: [BlockKey.Indicators, BlockKey.PolygonTools, BlockKey.Map, BlockKey.SaveButton]
        },
        claimDetail: {
            indicators: [
                { key: IndicatorKey.CreationDate, label: 'Fecha denuncia', format: IndicatorFormat.Date },
                { key: IndicatorKey.ClaimId, label: 'Nº denuncia' },
                { key: IndicatorKey.EventType, label: 'Tipo evento' },
                { key: IndicatorKey.EventDate, label: 'Fecha evento', format: IndicatorFormat.Date },
                { key: IndicatorKey.CropType, label: 'Tipo de cultivo' },
                { key: IndicatorKey.DeclaredArea, label: 'Área declarada', format: IndicatorFormat.Hectares, decimals: 1 },
                { key: IndicatorKey.Area, label: 'Área', format: IndicatorFormat.Hectares }
            ],
            blocks: [
                BlockKey.Indicators,
                BlockKey.MapLayers,
                BlockKey.SurfaceSummary,
                BlockKey.Inspection,
                BlockKey.Notes,
                BlockKey.ClimateChart
            ],
            inspectionTabs: [InspectionTab.Appraisers]
        }
    },
    // Perfiles por aseguradora (`customer` de `/me/`). `primary` queda el de `branding.colors` para todas.
    customers: {
        // Aseguradora 0: los colores de siempre del tenant.
        0: {
            colors: { brandBackground: '#003781', info: '#003781' }
        },
        // Aseguradora 1 (La Segunda): color de títulos y links de lasegunda.com.ar. Su logo es rojo: va sobre un
        // recuadro blanco para que no se pierda en el header.
        1: {
            colors: { brandBackground: '#b31130', info: '#b31130', logoBackground: '#ffffff' }
        },
        // Aseguradora 6 (Surco): color de títulos y links de surco.com.uy. Su logo es azul: va sobre un recuadro blanco
        // para que se distinga del header. En la vista de suscripción no muestra la provincia.
        6: {
            colors: { brandBackground: '#455ea6', info: '#455ea6', logoBackground: '#ffffff' },
            pages: {
                item: { hiddenIndicators: [IndicatorKey.Province] }
            }
        }
    }
};
